import { Event } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Button, Menu as MenuBase, styles, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';

import { EVENT, ICON, METRICS_EVENTS } from '../../helpers';
import { ACTION, STATE } from '../Booking.constants';
import { L10N } from '../Booking.l10n';
import * as style from '../Booking.module.css';

const Menu = ({ dataSource = {}, onPress = () => {}, onVisibleChange = () => {}, visible, ...others }) => {
  const { isDesktop } = useDevice();
  const { translate } = useLocale();
  const refContainer = useRef();

  useEffect(() => {
    onVisibleChange(isDesktop || false);

    !isDesktop &&
      refContainer?.current &&
      Event.publish(EVENT.FOOTER_HEIGHT_CHANGE, { height: refContainer?.current.offsetHeight });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  const handlePress = (action) => {
    if (!isDesktop) {
      onVisibleChange(false);
    }
    if (!action) return;

    if (action === ACTION.CANCEL) {
      Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.BOOKING_INTERACTION, action: 'cancel_intent' });
    }
    if (action === ACTION.SHOW_CALENDAR) {
      Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.BOOKING_INTERACTION, action: 'modify_dates_intent' });
    }

    onPress(action);
  };

  const { isPostponable = false, modify = {}, state = STATE.CONFIRMED } = dataSource;
  const { board, date = false, occupation } = modify;
  const boardRooms = Array.isArray(board?.rooms) ? board.rooms : [];
  const occupationRooms = Array.isArray(occupation?.rooms) ? occupation.rooms : [];
  const is = {
    cancelled: state === STATE.CANCELLED,
    confirmed: state === STATE.CONFIRMED,
    modifiedBoard: state === STATE.CONFIRMED && boardRooms.length > 0,
    modifiedDate: state === STATE.CONFIRMED && date,
    modifiedOccupation: state === STATE.CONFIRMED && occupationRooms.length > 0,
    postponable: state === STATE.CONFIRMED && isPostponable,
  };

  return (
    <View ref={refContainer} {...others} className={styles(style.menu, style.noPrint, others.className)}>
      <MenuBase
        options={[
          { icon: ICON.PRINT, label: translate(L10N.ACTION_PRINT), value: ACTION.PRINT, divider: true },
          { icon: ICON.MAIL, label: translate(L10N.ACTION_RESEND_BOOKING), value: ACTION.RESEND, divider: true },
          is.postponable
            ? {
                icon: ICON.CALENDAR_EDIT,
                label: translate(L10N.ACTION_EDIT_BOOKING),
                value: ACTION.EDIT,
                divider: true,
              }
            : undefined,
          is.confirmed
            ? {
                icon: ICON.CALENDAR_CANCEL,
                label: translate(L10N.ACTION_CANCEL_BOOKING),
                value: ACTION.CANCEL,
                divider: is.modifiedDate,
              }
            : undefined,
          is.modifiedDate
            ? {
                icon: ICON.CALENDAR_EDIT,
                label: translate(L10N.ACTION_EDIT_DATE),
                value: ACTION.SHOW_CALENDAR,
                divider: is.modifiedOccupation || is.modifiedBoard,
              }
            : undefined,
          is.modifiedOccupation
            ? {
                icon: ICON.GROUP,
                label: translate(L10N.ACTION_EDIT_OCCUPATION),
                value: ACTION.EDIT_OCCUPATION,
                divider: is.modifiedBoard,
              }
            : undefined,
          is.modifiedBoard
            ? {
                icon: ICON.LIST,
                label: translate(L10N.ACTION_EDIT_BOARD),
                value: ACTION.EDIT_BOARD,
              }
            : undefined,
        ]}
        visible={visible}
        onPress={handlePress}
        testId="booking-menu"
      />

      {!isDesktop && (
        <Button
          large
          wide
          onPress={() => {
            onVisibleChange(!visible);
          }}
          testId="show-booking-menu"
        >
          {translate(L10N.ACTION_SHOW_OPTIONS)}
        </Button>
      )}
    </View>
  );
};

Menu.displayName = 'Mirai:Core:Booking:Menu';

Menu.propTypes = {
  dataSource: PropTypes.shape({
    state: PropTypes.oneOf(Object.values(STATE)),
  }),
  onPress: PropTypes.func,
  onVisibleChange: PropTypes.func,
  visible: PropTypes.bool,
};

export { Menu };
